<script setup lang="ts">
  import SettingsPage from '@/components/app/settings/SettingsPage.vue'
  import CheckboxField from '@/components/admin/form/CheckboxField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import { useTranslation } from '@/composables/useTranslation'
  import { computed, onMounted, ref, watch } from 'vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import {
    type HubspotSettings,
    type HubspotSettingsRequest,
    type CustomerUser,
    Role5ccEnum,
    SettingsService
  } from '@/types/generated'
  import * as Sentry from '@sentry/vue'

  const isLoading = ref(false)
  const { fetchUser } = useUserStore()
  const user = computed((): CustomerUser => useUserStore().user as CustomerUser)

  // HubSpot integration fields
  const isHubspotEnabled = ref(false)
  const hubspotPrivateKey = ref('')
  const originalHubspotPrivateKey = ref('')

  const { t } = useTranslation({
    en: {
      warning: 'Warning',
      onlyAdminsAllowedMsg: 'Only the administrator is allowed to edit this setting.',
      description:
        'Configure HubSpot integration settings. This allows JUPUS to sync data with your HubSpot CRM.',
      enableHubspot: 'Enable HubSpot Integration',
      privateKey: 'Private Key',
      privateKeyPlaceholder: 'Enter HubSpot private key'
    },
    de: {
      warning: 'Achtung',
      onlyAdminsAllowedMsg: 'Nur der Administrator kann diese Einstellung bearbeiten.',
      description:
        'Konfigurieren Sie die HubSpot-Integrationseinstellungen. Damit kann JUPUS Daten mit Ihrem HubSpot CRM synchronisieren.',
      enableHubspot: 'HubSpot Integration aktivieren',
      privateKey: 'Private Key',
      privateKeyPlaceholder: 'HubSpot Private Key eingeben'
    }
  })

  const updateHubspotSettings = async () => {
    try {
      isLoading.value = true

      const data: HubspotSettingsRequest = {
        is_hubspot_egress_integration_enabled: isHubspotEnabled.value
      }

      // Only include private key if it has changed from the original
      if (hubspotPrivateKey.value !== originalHubspotPrivateKey.value) {
        data.hubspot_private_key = hubspotPrivateKey.value
      }

      await SettingsService.settingsHubspotCreate({
        requestBody: data
      })
    } catch (error) {
      Sentry.captureException(error)
    } finally {
      isLoading.value = false
    }
  }

  onMounted(async () => {
    if (!user.value) {
      await fetchUser()
    }

    try {
      const settings = (await SettingsService.settingsHubspotList()) as HubspotSettings

      if (settings) {
        isHubspotEnabled.value = settings.is_hubspot_egress_integration_enabled || false
        hubspotPrivateKey.value = settings.hubspot_private_key || ''
        originalHubspotPrivateKey.value = settings.hubspot_private_key || ''
      }
    } catch (error) {
      console.error('Error loading HubSpot settings:', error)
      Sentry.captureException(error)
    }
  })

  watch([isHubspotEnabled], () => {
    if (user.value) {
      updateHubspotSettings()
    }
  })

  watch(hubspotPrivateKey, () => {
    if (user.value) {
      updateHubspotSettings()
    }
  })
</script>

<template>
  <SettingsPage v-if="user">
    <div class="space-y-8">
      <div
        v-if="user.role !== Role5ccEnum.ADMIN"
        class="mb-6"
      >
        <div class="mb-4 w-full rounded-md bg-orange-100 p-4 text-orange-700">
          <p class="font-medium">{{ t('warning') }}</p>
          <p class="mt-2">{{ t('onlyAdminsAllowedMsg') }}</p>
        </div>
      </div>
      <div
        v-else
        class="mb-6"
      >
        <p class="mb-4 text-sm text-gray-600">{{ t('description') }}</p>

        <div class="max-w-xl space-y-4">
          <CheckboxField
            v-model="isHubspotEnabled"
            name="is_hubspot_egress_integration_enabled"
            :label="t('enableHubspot')"
            data-cy="hubspot-integration-enabled-checkbox"
          />

          <div v-if="isHubspotEnabled">
            <InputField
              v-model="hubspotPrivateKey"
              name="hubspot_private_key"
              :label="t('privateKey')"
              :placeholder="t('privateKeyPlaceholder')"
              type="password"
              data-cy="hubspot-private-key-input"
            />
          </div>
        </div>
      </div>
    </div>
  </SettingsPage>
</template>

<style scoped>
  /* Add any scoped styles here if needed */
</style>
